/* eslint-disable formatjs/no-literal-string-in-jsx */

import React, { FunctionComponent, useEffect, useState } from 'react'

import { isEqual } from 'lodash-es'
import styled, { useTheme } from 'styled-components/native'

import { UniversalSearchConfig } from '@lyrahealth-inc/shared-app-logic'

import { BaseInputField, BodyText, ButtonSize, ButtonType, SecondaryButton } from '../../../atoms'
import { BaseButton } from '../../../atoms/baseButton/BaseButton'
import { BodyTextSize } from '../../../styles'
import { tID } from '../../../utils'

const Container = styled.View({
  gap: 12,
})

const FieldRow = styled.View({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
})

const FieldLabel = styled(BodyText)({
  flex: 1,
})

const FieldContent = styled.View({
  flex: 1,
})

const InputRow = styled.View({
  flexDirection: 'row',
  alignItems: 'center',
  gap: 8,
})

const InputContainer = styled.View({
  width: 80,
})

const ButtonContainer = styled.View({
  marginTop: 16,
  flexDirection: 'row',
  gap: 12,
})

interface ConfigField {
  key: keyof UniversalSearchConfig
  label: string
  step: number
  min: number
  max: number
  hint: string
}

const configFields: ConfigField[] = [
  { key: 'standardThreshold', label: 'Standard Threshold', step: 0.05, min: 0, max: 1, hint: '0-1, lower = stricter' },
  { key: 'strictThreshold', label: 'Strict Threshold', step: 0.05, min: 0, max: 1, hint: '0-1, lower = stricter' },
  { key: 'minQueryLength', label: 'Min Query Length', step: 1, min: 1, max: 10, hint: '1-10 characters' },
  {
    key: 'displayNameWeight',
    label: 'Display Name Weight',
    step: 1,
    min: 1,
    max: 10,
    hint: '1-10, higher = more important',
  },
  { key: 'keywordsWeight', label: 'Keywords Weight', step: 1, min: 1, max: 10, hint: '1-10, higher = more important' },
  { key: 'debounceMs', label: 'Debounce (ms)', step: 50, min: 0, max: 2000, hint: '0-2000ms' },
  {
    key: 'scoreDiffThreshold',
    label: 'Score Diff Threshold',
    step: 0.001,
    min: 0,
    max: 0.1,
    hint: 'How close two scores must be to rank by category instead of score. At 0, exact scores determine order. At 0.001, results within 0.001 of each other (e.g., 0.0000 vs 0.0008) are treated as equally relevant, so category priority decides their order.',
  },
]

export interface SearchConfigFormProps {
  /** Current config values (merged LD + DevTools overrides) */
  config: UniversalSearchConfig
  /** Base config from LaunchDarkly (used for comparison indicator and reset) */
  baseConfig: UniversalSearchConfig
  /** Called with the full validated config */
  onConfigChange: (config: UniversalSearchConfig) => void
  onReset: () => void
}

export const SearchConfigForm: FunctionComponent<SearchConfigFormProps> = ({
  config,
  baseConfig,
  onConfigChange,
  onReset,
}) => {
  const { colors } = useTheme()
  const {
    standardThreshold,
    strictThreshold,
    minQueryLength,
    displayNameWeight,
    keywordsWeight,
    debounceMs,
    scoreDiffThreshold,
  } = config
  // Store raw string values for editing (allows typing decimals, backspacing, etc.)
  const [inputValues, setInputValues] = useState<Record<keyof UniversalSearchConfig, string>>(() => ({
    standardThreshold: String(standardThreshold),
    strictThreshold: String(strictThreshold),
    minQueryLength: String(minQueryLength),
    displayNameWeight: String(displayNameWeight),
    keywordsWeight: String(keywordsWeight),
    debounceMs: String(debounceMs),
    scoreDiffThreshold: String(scoreDiffThreshold),
  }))

  // Sync input values when config prop changes (e.g., after reset)
  useEffect(() => {
    setInputValues({
      standardThreshold: String(config.standardThreshold),
      strictThreshold: String(config.strictThreshold),
      minQueryLength: String(config.minQueryLength),
      displayNameWeight: String(config.displayNameWeight),
      keywordsWeight: String(config.keywordsWeight),
      debounceMs: String(config.debounceMs),
      scoreDiffThreshold: String(config.scoreDiffThreshold),
    })
  }, [config])

  const handleValueChange = (key: keyof UniversalSearchConfig, value: string) => {
    setInputValues((prev) => ({ ...prev, [key]: value }))
  }

  const getFieldError = (field: ConfigField): string | null => {
    const rawValue = inputValues[field.key]
    if (rawValue.trim() === '') {
      return 'Required'
    }
    const numValue = parseFloat(rawValue)
    if (isNaN(numValue)) {
      return 'Must be a number'
    }
    if (numValue < field.min || numValue > field.max) {
      return `Must be ${field.min}-${field.max}`
    }
    return null
  }

  const hasValidationErrors = configFields.some((field) => getFieldError(field) !== null)

  const getValidatedConfig = (): UniversalSearchConfig => {
    const validated: UniversalSearchConfig = { ...config }
    for (const field of configFields) {
      const rawValue = inputValues[field.key]
      const numValue = parseFloat(rawValue)
      if (!isNaN(numValue)) {
        validated[field.key] = Math.max(field.min, Math.min(field.max, numValue))
      }
    }
    return validated
  }

  const hasChanges = !isEqual(getValidatedConfig(), config)

  const handleSubmit = () => {
    const validated = getValidatedConfig()
    const {
      standardThreshold,
      strictThreshold,
      minQueryLength,
      displayNameWeight,
      keywordsWeight,
      debounceMs,
      scoreDiffThreshold,
    } = validated
    onConfigChange(validated)
    // Update input values to reflect clamped values
    setInputValues({
      standardThreshold: String(standardThreshold),
      strictThreshold: String(strictThreshold),
      minQueryLength: String(minQueryLength),
      displayNameWeight: String(displayNameWeight),
      keywordsWeight: String(keywordsWeight),
      debounceMs: String(debounceMs),
      scoreDiffThreshold: String(scoreDiffThreshold),
    })
  }

  const handleReset = () => {
    // Reset to baseConfig (LD values)
    const {
      standardThreshold,
      strictThreshold,
      minQueryLength,
      displayNameWeight,
      keywordsWeight,
      debounceMs,
      scoreDiffThreshold,
    } = baseConfig
    setInputValues({
      standardThreshold: String(standardThreshold),
      strictThreshold: String(strictThreshold),
      minQueryLength: String(minQueryLength),
      displayNameWeight: String(displayNameWeight),
      keywordsWeight: String(keywordsWeight),
      debounceMs: String(debounceMs),
      scoreDiffThreshold: String(scoreDiffThreshold),
    })
    onReset()
  }

  // Compare against baseConfig (LD values) to show indicator for overridden values
  const isBaseValue = (key: keyof UniversalSearchConfig) => {
    const numValue = parseFloat(inputValues[key])
    return !isNaN(numValue) && numValue === baseConfig[key]
  }

  return (
    <Container testID={tID('SearchConfigForm')}>
      <BodyText size={BodyTextSize.SMALL} color={colors.textSecondary} text='Fuse.js configuration values:' />
      {configFields.map((field) => {
        const error = getFieldError(field)
        return (
          <FieldRow key={field.key}>
            <FieldContent>
              <InputRow>
                <FieldLabel
                  size={BodyTextSize.SMALL}
                  text={`${field.label}${isBaseValue(field.key) ? '' : ' *'}`}
                  color={error ? colors.textError : isBaseValue(field.key) ? colors.textPrimary : colors.textActive}
                />
                <InputContainer>
                  <BaseInputField
                    inputValue={inputValues[field.key]}
                    onInputChange={(value) => handleValueChange(field.key, value)}
                    borderWidth={1}
                    height={32}
                    multiline={false}
                    width='100%'
                  />
                </InputContainer>
              </InputRow>
              <BodyText
                size={BodyTextSize.CAPTION}
                color={error ? colors.textError : colors.textSecondary}
                text={error ?? field.hint}
              />
            </FieldContent>
          </FieldRow>
        )
      })}
      <BodyText size={BodyTextSize.SMALL} color={colors.textSecondary} text='* indicates overridden value' />
      {hasChanges && <BodyText size={BodyTextSize.SMALL} color={colors.textWarning} text='You have unsaved changes' />}
      <ButtonContainer>
        <BaseButton
          buttonType={ButtonType.PRIMARY}
          size={ButtonSize.SMALL}
          text='Apply Config'
          onPress={handleSubmit}
          disabled={hasValidationErrors}
          testID={tID('SearchConfigForm-submit')}
        />
        <SecondaryButton
          isSmall
          text='Reset to Defaults'
          onPress={handleReset}
          testID={tID('SearchConfigForm-reset')}
        />
      </ButtonContainer>
    </Container>
  )
}
